import { Avatar } from 'antd';
import { AvatarProps } from 'antd/lib/avatar';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { ReadingText, SubText } from '../Typography';
import defaultImage from './no-image.png';

export interface User {
  /**
   * avatar props. Ref: https://ant.design/components/avatar/
   */
  avatar?: AvatarProps;
  name?: string | React.ReactNode;
  info?: string | React.ReactNode;
  authorAboutURL?: string;
  className?: string;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  user: User;
}

const CUSTOM_SIZE = {
  large: 80,
  normal: 40,
  small: 16,
};

export const useAvatarSize = (size: AvatarProps['size']) =>
  React.useMemo(() => {
    let result = size || CUSTOM_SIZE.normal;

    if (size && typeof size === 'string' && Object.keys(CUSTOM_SIZE).includes(size)) {
      result = CUSTOM_SIZE[size];
    }

    return result;
  }, [size]);

const Aavatar = ({ user: { avatar, name, info, authorAboutURL }, ...props }: Props) => {
  const size = useAvatarSize(avatar.size);

  return (
    <Container {...props}>
      <AvatarWrapper transparent={avatar.icon}>
        <Avatar
          {...avatar}
          size={size}
          src={avatar.src || (!avatar.icon && defaultImage)}
          style={{
            backgroundColor: 'transparent',
          }}
          alt={avatar.alt}
        />
      </AvatarWrapper>
      {name && <UserInfo name={name} info={info} authorAboutURL={authorAboutURL} />}
    </Container>
  );
};

export const UserInfo = ({ name, info, authorAboutURL, ...props }: User) => {
  const authorName = React.useMemo(() => {
    if (React.isValidElement(name)) {
      return name;
    }
    return <ReadingText className="avatar-name">{name}</ReadingText>;
  }, [name]);

  return (
    <Right {...props}>
      {authorName}
      {React.isValidElement(info) ? info : <SubText className="avatar-info">{info}</SubText>}
    </Right>
  );
};

const Right = styled.div.withConfig({
  componentId: 'Avatar_Right',
} as any)`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  margin-left: 12px;

  a {
    &:hover {
      text-decoration: underline solid ${colorsV2.green100};
    }
  }
`;

const AvatarWrapper = styled.div.withConfig({
  componentId: 'Avatar_AvatarWrapper',
} as any)<{ transparent: any }>`
  border-radius: 100%;
  background-color: ${colorsV2.gray20};
  .ant-avatar-image {
    display: block;
  }
`;

export const Container = styled.div.withConfig({
  componentId: 'Avatar_Container',
} as any)`
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;

  &:hover {
    z-index: 1;
  }
`;

export default React.memo(Aavatar);
